import { CopyOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  DatePicker,
  Form,
  Image,
  Input,
  InputNumber,
  Select,
  Space,
  Tag,
  Typography,
  message,
} from 'antd';
import Table, { ColumnsType } from 'antd/es/table';
import commonApi from 'apis/common';
import { getFileUploadLogList } from 'apis/sys';
import usePagination from 'commons/hooks/usePagination';
import LOCALS from 'commons/locals';
import dayjs, { Dayjs } from 'dayjs';
import i18n from 'i18n';
import { parseAsInteger, parseAsString, useQueryState } from 'nuqs';
import FileUploadLogTrend from 'pages/sys/file-upload-log-trend';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { Link } from 'react-router-dom';
import copyToClipboard from 'utils/copyToClipboard';
import formatTime from 'utils/formatTime';
import getClientTimeZone from 'utils/getClientTimeZone';

type FileUploadLog = Awaited<
  ReturnType<typeof getFileUploadLogList>
>['list'][number];

const OPERATOR_TYPE_OPTIONS = [
  { label: '管理员', value: 'admin' },
  { label: '会员', value: 'member' },
  { label: '系统', value: 'system' },
] as const;

const FILE_UPLOAD_SOURCE_OPTIONS = [
  { label: '全部后台', value: 'admin/' },
  { label: '全部官网', value: 'website/' },
  { label: '全部 App', value: 'app/' },
  { label: '官网通用上传', value: 'website/common/upload-file' },
  { label: 'App 通用上传', value: 'app/common/upload-file' },
  { label: '后台通用上传', value: 'admin/common/upload-file' },
  { label: '商品图上传', value: 'admin/common/upload-product-image' },
  { label: '假包图上传', value: 'admin/common/upload-fake-bag-image' },
  { label: '抠图', value: 'admin/common/photo-room' },
  { label: '图片处理', value: 'admin/common/image-process' },
  { label: '鉴定单图', value: 'admin/authentication-bag/upload' },
  { label: '打款凭证', value: 'admin/oms-recycle-order/payment-voucher' },
  { label: '回收合同', value: 'admin/oms-recycle-order/signed-contract' },
  { label: '订单图片上传', value: 'admin/order/upload-file' },
  { label: '订单签名', value: 'order/generate/sign' },
  { label: '帖子创建', value: 'website/xiaoma-posts/create' },
  { label: 'IM 客服上传', value: 'im-chat/manager/upload-file' },
  { label: 'IM 用户上传', value: 'im-chat/user/upload-file' },
  { label: '未知', value: 'unknown' },
];

const SOURCE_LABEL_MAP: Record<string, string> = {
  'website/common/upload-file': '官网通用上传',
  'app/common/upload-file': 'App 通用上传',
  'admin/common/upload-file': '后台通用上传',
  'admin/common/upload-product-image': '商品图上传',
  'admin/common/upload-fake-bag-image': '假包图上传',
  'admin/common/photo-room': '抠图',
  'admin/common/image-process': '图片处理',
  'admin/authentication-bag/upload': '鉴定单图',
  'admin/oms-recycle-order/payment-voucher': '打款凭证',
  'admin/oms-recycle-order/signed-contract': '回收合同',
  'admin/order/upload-file': '订单图片上传',
  'order/generate/sign': '订单签名',
  'website/xiaoma-posts/create': '帖子创建',
  'im-chat/manager/upload-file': 'IM 客服上传',
  'im-chat/user/upload-file': 'IM 用户上传',
  unknown: '未知',
};

const MIME_TYPE_OPTIONS = [
  { label: '图片', value: 'image/' },
  { label: '视频', value: 'video/' },
  { label: 'PDF', value: 'application/pdf' },
  { label: '其他文件', value: 'application/' },
];

const CopySignedUrlButton = ({ s3Key }: { s3Key: string }) => {
  const [loading, setLoading] = useState(false);

  const handleCopy = async () => {
    try {
      setLoading(true);
      const { url } = await commonApi.getSignedFileUrl(s3Key);
      if (!url) {
        message.error('获取 signed URL 失败');
        return;
      }
      copyToClipboard(url);
      message.success('已复制 signed URL');
    } catch {
      // 接口错误已由请求拦截器提示
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      type="link"
      size="small"
      className="px-0"
      icon={<CopyOutlined />}
      loading={loading}
      onClick={handleCopy}
    >
      复制 signed URL
    </Button>
  );
};

const formatFileSize = (bytes?: number | null) => {
  if (bytes == null) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const operatorTypeTag = (type: FileUploadLog['operatorType']) => {
  if (type === 'admin') return <Tag color="blue">管理员</Tag>;
  if (type === 'member') return <Tag color="green">会员</Tag>;
  if (type === 'system') return <Tag>系统</Tag>;
  return type || '-';
};

const useQueryParams = () => {
  const [pageNum, setPageNum] = useQueryState(
    'pageNum',
    parseAsInteger.withDefault(1),
  );
  const [pageSize, setPageSize] = useQueryState(
    'pageSize',
    parseAsInteger.withDefault(10),
  );
  const [operatorType, setOperatorType] = useQueryState(
    'operatorType',
    parseAsString.withDefault(''),
  );
  const [operatorId, setOperatorId] = useQueryState(
    'operatorId',
    parseAsInteger,
  );
  const [operatorName, setOperatorName] = useQueryState(
    'operatorName',
    parseAsString.withDefault(''),
  );
  const [source, setSource] = useQueryState(
    'source',
    parseAsString.withDefault(''),
  );
  const [originalName, setOriginalName] = useQueryState(
    'originalName',
    parseAsString.withDefault(''),
  );
  const [mimeType, setMimeType] = useQueryState(
    'mimeType',
    parseAsString.withDefault(''),
  );
  const [isPrivate, setIsPrivate] = useQueryState('isPrivate', parseAsInteger);
  const [startTime, setStartTime] = useQueryState(
    'startTime',
    parseAsString.withDefault(''),
  );
  const [endTime, setEndTime] = useQueryState(
    'endTime',
    parseAsString.withDefault(''),
  );

  return {
    pageNum,
    pageSize,
    operatorType,
    operatorId,
    operatorName,
    source,
    originalName,
    mimeType,
    isPrivate,
    startTime,
    endTime,
    setPageNum,
    setPageSize,
    setOperatorType,
    setOperatorId,
    setOperatorName,
    setSource,
    setOriginalName,
    setMimeType,
    setIsPrivate,
    setStartTime,
    setEndTime,
  };
};

const FileUploadLogPage = () => {
  const {
    pageNum,
    pageSize,
    operatorType,
    operatorId,
    operatorName,
    source,
    originalName,
    mimeType,
    isPrivate,
    startTime,
    endTime,
    setPageNum,
    setPageSize,
    setOperatorType,
    setOperatorId,
    setOperatorName,
    setSource,
    setOriginalName,
    setMimeType,
    setIsPrivate,
    setStartTime,
    setEndTime,
  } = useQueryParams();

  const { loading, setLoading, total, setTotal, dataSource, setDataSource } =
    usePagination<FileUploadLog>();

  const getDataSource = useCallback(async () => {
    try {
      setLoading(true);
      const { list, total } = await getFileUploadLogList({
        pageNum,
        pageSize,
        operatorType:
          operatorType === 'member' ||
          operatorType === 'admin' ||
          operatorType === 'system'
            ? operatorType
            : undefined,
        operatorId: operatorId ?? undefined,
        operatorName: operatorName || undefined,
        source: source || undefined,
        originalName: originalName || undefined,
        mimeType: mimeType || undefined,
        isPrivate: isPrivate === 0 || isPrivate === 1 ? isPrivate : undefined,
        startTime: startTime || undefined,
        endTime: endTime || undefined,
        timeZone: getClientTimeZone(),
      });
      setDataSource(list);
      setTotal(total);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  }, [
    endTime,
    isPrivate,
    mimeType,
    operatorId,
    operatorName,
    operatorType,
    originalName,
    pageNum,
    pageSize,
    setDataSource,
    setLoading,
    setTotal,
    source,
    startTime,
  ]);

  useEffect(() => {
    getDataSource();
  }, [getDataSource]);

  const dateRange = useMemo<[Dayjs | null, Dayjs | null] | null>(() => {
    if (!startTime && !endTime) return null;
    return [
      startTime ? dayjs(startTime) : null,
      endTime ? dayjs(endTime) : null,
    ];
  }, [endTime, startTime]);

  const columns: ColumnsType<FileUploadLog> = useMemo(
    () => [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        width: 90,
      },
      {
        title: '上传时间',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 180,
        render: (createdAt?: string) => formatTime(createdAt),
      },
      {
        title: '操作人类型',
        dataIndex: 'operatorType',
        key: 'operatorType',
        width: 110,
        render: (value: FileUploadLog['operatorType']) =>
          operatorTypeTag(value),
      },
      {
        title: '操作人',
        key: 'operator',
        width: 180,
        render: (_, record) => {
          const name = record.operatorName || '-';
          const idText =
            record.operatorId != null ? ` (#${record.operatorId})` : '';

          if (record.operatorType === 'member' && record.operatorId != null) {
            return (
              <>
                <Link to={`/ums/member-view/${record.operatorId}`}>{name}</Link>
                {idText}
              </>
            );
          }

          return `${name}${idText}`;
        },
      },
      {
        title: '来源',
        dataIndex: 'source',
        key: 'source',
        width: 220,
        render: (value?: string) => {
          if (!value) return '-';
          const label = SOURCE_LABEL_MAP[value];
          return label ? (
            <div>
              <div>{label}</div>
              <div className="text-xs text-gray-400">{value}</div>
            </div>
          ) : (
            value
          );
        },
      },
      {
        title: '文件',
        key: 'file',
        width: 260,
        render: (_, record) => {
          const isImage = record.mimeType?.startsWith('image/');
          return (
            <Space align="center">
              {isImage && !record.isPrivate && record.url ? (
                <Image
                  src={record.url}
                  width={48}
                  height={48}
                  style={{ objectFit: 'cover' }}
                />
              ) : null}
              <div className="truncate max-w-[180px]">
                {record.originalName || '-'}
              </div>
            </Space>
          );
        },
      },
      {
        title: 'MIME',
        dataIndex: 'mimeType',
        key: 'mimeType',
        width: 140,
        render: (value?: string | null) => value || '-',
      },
      {
        title: '大小',
        dataIndex: 'size',
        key: 'size',
        width: 100,
        render: (value?: number | null) => formatFileSize(value),
      },
      {
        title: '私有',
        dataIndex: 'isPrivate',
        key: 'isPrivate',
        width: 80,
        render: (value?: number) =>
          value === 1 ? (
            <Tag color="orange">私有</Tag>
          ) : (
            <Tag color="default">公开</Tag>
          ),
      },
      {
        title: 'IP',
        dataIndex: 'ip',
        key: 'ip',
        width: 140,
        render: (value?: string | null) => value || '-',
      },
      {
        title: '位置',
        dataIndex: 'address',
        key: 'address',
        width: 180,
        render: (value?: string | null) => value || '-',
      },
      {
        title: 'User-Agent',
        dataIndex: 'userAgent',
        key: 'userAgent',
        width: 280,
        render: (value?: string | null) =>
          value ? (
            <Typography.Text
              copyable={{ tooltips: ['复制 User-Agent', '已复制'] }}
              ellipsis={{ tooltip: value }}
            >
              {value}
            </Typography.Text>
          ) : (
            '-'
          ),
      },
      {
        title: 'URL',
        dataIndex: 'url',
        key: 'url',
        width: 280,
        render: (value: string | undefined, record) => (
          <div className="flex flex-col gap-1">
            {value ? (
              <Typography.Text
                copyable={{ tooltips: ['复制 URL', '已复制'] }}
                ellipsis={{ tooltip: value }}
              >
                {value}
              </Typography.Text>
            ) : (
              '-'
            )}
            {record.isPrivate === 1 && record.s3Key ? (
              <CopySignedUrlButton s3Key={record.s3Key} />
            ) : null}
          </div>
        ),
      },
      {
        title: 'S3 Key',
        dataIndex: 's3Key',
        key: 's3Key',
        width: 280,
        render: (value?: string) =>
          value ? (
            <Typography.Text copyable ellipsis={{ tooltip: value }}>
              {value}
            </Typography.Text>
          ) : (
            '-'
          ),
      },
    ],
    [],
  );

  return (
    <div className="space-y-4">
      <FileUploadLogTrend />

      <Card bodyStyle={{ padding: '16px' }}>
        <Form layout="inline">
          <Form.Item label="操作人类型" className="w-60">
            <Select
              value={operatorType || undefined}
              onChange={(value) => {
                setOperatorType(value || '');
                setPageNum(1);
              }}
              allowClear
              options={[...OPERATOR_TYPE_OPTIONS]}
              placeholder={<Trans i18nKey={LOCALS.please_select} />}
            />
          </Form.Item>

          <Form.Item label="操作人">
            <Input
              value={operatorName}
              onChange={(e) => {
                setOperatorName(e.target.value);
                setPageNum(1);
              }}
              placeholder="用户名 / 邮箱"
              allowClear
            />
          </Form.Item>

          <Form.Item label="操作人 ID">
            <InputNumber
              className="w-40"
              value={operatorId ?? undefined}
              onChange={(value) => {
                setOperatorId(typeof value === 'number' ? value : null);
                setPageNum(1);
              }}
              min={1}
              placeholder={i18n.t(LOCALS.please_enter) || ''}
            />
          </Form.Item>

          <Form.Item label="来源" className="w-72">
            <Select
              value={source || undefined}
              onChange={(value) => {
                setSource(value || '');
                setPageNum(1);
              }}
              allowClear
              showSearch
              optionFilterProp="label"
              options={FILE_UPLOAD_SOURCE_OPTIONS}
              placeholder={<Trans i18nKey={LOCALS.please_select} />}
            />
          </Form.Item>

          <Form.Item label="文件名">
            <Input
              value={originalName}
              onChange={(e) => {
                setOriginalName(e.target.value);
                setPageNum(1);
              }}
              placeholder={i18n.t(LOCALS.please_enter) || ''}
              allowClear
            />
          </Form.Item>

          <Form.Item label="MIME" className="w-60">
            <Select
              value={mimeType || undefined}
              onChange={(value) => {
                setMimeType(value || '');
                setPageNum(1);
              }}
              allowClear
              options={MIME_TYPE_OPTIONS}
              placeholder={<Trans i18nKey={LOCALS.please_select} />}
            />
          </Form.Item>

          <Form.Item label="私有" className="w-48">
            <Select
              value={isPrivate === 0 || isPrivate === 1 ? isPrivate : undefined}
              onChange={(value) => {
                setIsPrivate(value === 0 || value === 1 ? value : null);
                setPageNum(1);
              }}
              allowClear
              options={[
                { label: '公开', value: 0 },
                { label: '私有', value: 1 },
              ]}
              placeholder={<Trans i18nKey={LOCALS.please_select} />}
            />
          </Form.Item>

          <Form.Item label="上传时间">
            <DatePicker.RangePicker
              value={dateRange}
              onChange={(dates) => {
                setStartTime(
                  dates?.[0]
                    ? dates[0].startOf('day').format('YYYY-MM-DD HH:mm:ss')
                    : '',
                );
                setEndTime(
                  dates?.[1]
                    ? dates[1].endOf('day').format('YYYY-MM-DD HH:mm:ss')
                    : '',
                );
                setPageNum(1);
              }}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                htmlType="button"
                onClick={() => {
                  setOperatorType('');
                  setOperatorId(null);
                  setOperatorName('');
                  setSource('');
                  setOriginalName('');
                  setMimeType('');
                  setIsPrivate(null);
                  setStartTime('');
                  setEndTime('');
                  setPageNum(1);
                  setPageSize(10);
                }}
              >
                <Trans i18nKey={LOCALS.reset} />
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      <Card bodyStyle={{ padding: '16px' }}>
        <Table
          bordered
          pagination={{
            total,
            pageSize,
            current: pageNum,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50, 100],
            onChange: (page, nextPageSize) => {
              setPageNum(page);
              setPageSize(nextPageSize);
            },
          }}
          loading={loading}
          rowKey="id"
          dataSource={dataSource}
          columns={columns}
          scroll={{ x: 'max-content' }}
          tableLayout="auto"
        />
      </Card>
    </div>
  );
};

export default FileUploadLogPage;
